// src/components/map/hooks/adapters/useMapFacilityHighlighting.ts
//
// Adapter hook: Facility Highlighting
//
// Owns:
//   - getFacilityIconSizeExpression() (base facility icon sizing)
//   - getFacilityHighlightIconSizeExpression() (conditional highlight sizing)
//   - getHighlightedFacilityOverlayIconSizeExpression() (overlay layer sizing)
//   - highlightFacilities() (creates highlight overlay layer)
//   - clearHighlightedFacilities() (removes overlay, resets sizing)
//
// Mapbox layers/sources managed:
//   - HIGHLIGHTED_FACILITIES (source + layer)
//   - FACILITY_LAYER icon-size manipulation
//   - CLICKED_FACILITY icon-size manipulation
//
// Store reads: selectDisplaySettings (facilityIconSize)
//
// This hook was extracted from MapboxWrapper as part of the concern-based
// decomposition (Phase 6).
import * as Sentry from "@sentry/react";
import { MutableRefObject, useCallback } from "react";

import { feature, featureCollection } from "@turf/helpers";
import {
  FACILITY_LAYER,
  FACILITY_TYPE_ENUM,
  SPIDER_FACILITY_LAYER,
  SPIDER_FACILITY_SOURCE
} from "constants/mapLayers.constants";
import mapboxgl, { Expression } from "mapbox-gl";
import { selectDisplaySettings, useMapStore } from "stores/map";

import {
  buildFacilityHighlightIconSizeExpression,
  buildFacilityOverlayIconSizeExpression
} from "../../utils/facilityIconSizeExpressions";
import { buildFacilityIconSizeExpression } from "../../utils/networkMapStyles";

// ── Constants ────────────────────────────────────────────────────────────────

const HIGHLIGHTED_FACILITIES = "highlighted-facilities";
const CLICKED_FACILITY = "clicked-facility";

// ── Types ────────────────────────────────────────────────────────────────────

export interface UseMapFacilityHighlightingParams {
  /** Ref to the Mapbox GL map instance. */
  readonly mapboxRef: MutableRefObject<Maybe<mapboxgl.Map>>;
}

export interface UseMapFacilityHighlightingReturn {
  /** Create a highlight overlay layer for the given facility features. */
  readonly highlightFacilities: (facilities: mapboxgl.MapboxGeoJSONFeature[]) => void;
  /** Remove the highlight overlay and reset icon sizes to defaults. */
  readonly clearHighlightedFacilities: () => void;
  /** Build the base facility icon-size expression (zoom interpolation × user multiplier). */
  readonly getFacilityIconSizeExpression: () => Expression;
  /** Build facility icon-size expression that enlarges UWIs present in `data`. */
  readonly getFacilityHighlightIconSizeExpression: (
    data: Record<string, number>
  ) => Expression;
  /** Build icon-size expression for the highlighted-facility overlay layer. */
  readonly getHighlightedFacilityOverlayIconSizeExpression: () => Expression;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useMapFacilityHighlighting({
  mapboxRef
}: UseMapFacilityHighlightingParams): UseMapFacilityHighlightingReturn {
  // ── Zustand selectors ──────────────────────────────────────────────────
  const displaySettings = useMapStore(selectDisplaySettings);

  // ── Icon-size expression builders ──────────────────────────────────────

  const getFacilityIconSizeExpression = useCallback((): Expression => {
    const sizeMultiplier = displaySettings.facilityIconSize || 1;
    return buildFacilityIconSizeExpression(sizeMultiplier) as Expression;
  }, [displaySettings.facilityIconSize]);

  const getFacilityHighlightIconSizeExpression = useCallback(
    (facilityData: Record<string, number>): Expression => {
      const sizeMultiplier = displaySettings.facilityIconSize || 1;
      return buildFacilityHighlightIconSizeExpression(
        sizeMultiplier,
        facilityData
      ) as Expression;
    },
    [displaySettings.facilityIconSize]
  );

  const getHighlightedFacilityOverlayIconSizeExpression = useCallback((): Expression => {
    const sizeMultiplier = displaySettings.facilityIconSize || 1;
    return buildFacilityOverlayIconSizeExpression(sizeMultiplier) as Expression;
  }, [displaySettings.facilityIconSize]);

  // ── clearHighlightedFacilities ─────────────────────────────────────────

  const clearHighlightedFacilities = useCallback(() => {
    const mapbox = mapboxRef.current;
    if (!mapbox) return;

    if (mapbox.getLayer(HIGHLIGHTED_FACILITIES)) {
      mapbox.removeLayer(HIGHLIGHTED_FACILITIES);
    }
    if (mapbox.getSource(HIGHLIGHTED_FACILITIES)) {
      mapbox.removeSource(HIGHLIGHTED_FACILITIES);
    }

    if (mapbox.getLayer(FACILITY_LAYER)) {
      mapbox.setLayoutProperty(
        FACILITY_LAYER,
        "icon-size",
        getFacilityIconSizeExpression()
      );
    }

    if (mapbox.getLayer(CLICKED_FACILITY)) {
      mapbox.setLayoutProperty(CLICKED_FACILITY, "icon-size", [
        "interpolate",
        ["linear"],
        ["zoom"],
        6,
        0.3,
        10,
        1.1,
        12,
        1.6,
        22,
        2.1
      ]);
    }
  }, [mapboxRef, getFacilityIconSizeExpression]);

  // ── highlightFacilities ────────────────────────────────────────────────

  const highlightFacilities = useCallback(
    (facilities: mapboxgl.MapboxGeoJSONFeature[]) => {
      if (!mapboxRef.current || !facilities || facilities.length === 0) {
        return;
      }

      const mapbox = mapboxRef.current;

      try {
        const highlightedFeatures = facilities.map((feat) => {
          return feature(feat.geometry, {
            ...feat.properties,
            highlighted: true
          });
        });

        const highlightedFacilitiesCollection = featureCollection(highlightedFeatures);

        // Remove existing highlight layers first (but don't reset sizes yet)
        if (mapbox.getLayer(HIGHLIGHTED_FACILITIES)) {
          mapbox.removeLayer(HIGHLIGHTED_FACILITIES);
        }
        if (mapbox.getSource(HIGHLIGHTED_FACILITIES)) {
          mapbox.removeSource(HIGHLIGHTED_FACILITIES);
        }

        let isSpiderFacilityHighlighted = false;
        if (
          mapbox.getLayer(SPIDER_FACILITY_LAYER) &&
          highlightedFacilitiesCollection?.features
        ) {
          const highlightedUwis = highlightedFacilitiesCollection.features.map(
            (feat: { properties: Record<string, unknown> }) => feat.properties.Uwi
          );

          const spiderFacilityFeatures =
            mapbox.querySourceFeatures(SPIDER_FACILITY_SOURCE);

          // Check if any of the highlighted facilities are in the spider layer
          isSpiderFacilityHighlighted = spiderFacilityFeatures.some((feature) =>
            highlightedUwis.includes(feature.properties?.Uwi)
          );
        }
        if (
          (highlightedFacilitiesCollection?.features &&
            highlightedFacilitiesCollection.features.length > 1) ||
          isSpiderFacilityHighlighted
        ) {
          return;
        }

        const facilityData: Record<string, number> = {};
        for (const feat of facilities) {
          facilityData[feat.properties?.Uwi] = 2.0;
        }

        if (mapbox.getLayer(FACILITY_LAYER)) {
          mapbox.setLayoutProperty(
            FACILITY_LAYER,
            "icon-size",
            getFacilityHighlightIconSizeExpression(facilityData)
          );
        }

        if (mapbox.getLayer(CLICKED_FACILITY)) {
          const clickedFacilityFeatures = mapbox.querySourceFeatures(CLICKED_FACILITY);
          const hoveredUwis = facilities.map((f) => f.properties?.Uwi);
          const shouldResizeClickedFacility = clickedFacilityFeatures.some((feature) =>
            hoveredUwis.includes(feature.properties?.Uwi)
          );

          if (shouldResizeClickedFacility) {
            mapbox.setLayoutProperty(CLICKED_FACILITY, "icon-size", [
              "interpolate",
              ["linear"],
              ["zoom"],
              6,
              0.6,
              10,
              1.8,
              12,
              2.2,
              22,
              3.0
            ]);
          }
        }

        if (!mapboxRef.current) return;

        const highlightOverlayIconSize =
          getHighlightedFacilityOverlayIconSizeExpression() as Expression;

        mapbox.addSource(HIGHLIGHTED_FACILITIES, {
          type: "geojson",
          data: highlightedFacilitiesCollection
        });

        mapbox.addLayer({
          id: HIGHLIGHTED_FACILITIES,
          type: "symbol",
          source: HIGHLIGHTED_FACILITIES,
          paint: {
            "icon-color": "black"
          },
          layout: {
            "icon-image": [
              "match",
              ["get", "facilityType"],
              "CompressorStation",
              "compressor-station",
              FACILITY_TYPE_ENUM.GasPlant,
              "gas-plant",
              "Battery",
              "battery",
              "MeteringStation",
              "metering-station",
              "WastePlant",
              "waste-plant",
              "InjectionDisposalFacility",
              "injection-facility",
              "GatheringSystem",
              "gathering-system",
              /* default */ "default-facility"
            ],
            "icon-size": highlightOverlayIconSize,
            "icon-allow-overlap": true,
            "icon-ignore-placement": true
          }
        });
      } catch (error) {
        Sentry.withScope((scope) => {
          scope.setContext("facility_highlighting", {
            action: "highlightFacilities",
            status: "failed"
          });
          scope.setLevel("error");
          Sentry.captureException(error);
        });
      }
    },
    [
      mapboxRef,
      getFacilityHighlightIconSizeExpression,
      getHighlightedFacilityOverlayIconSizeExpression
    ]
  );

  return {
    highlightFacilities,
    clearHighlightedFacilities,
    getFacilityIconSizeExpression,
    getFacilityHighlightIconSizeExpression,
    getHighlightedFacilityOverlayIconSizeExpression
  };
}
